<cs-header>
  <cs-header-title>
    <span class="title-row">
      <span>{{ pageTitle }}</span>
      @if (hasWorkoutOptions) {
        <cs-icon-button large icon="ellipsis-vertical"
        (click)="showWorkoutOptions($event)"></cs-icon-button>
      }
    </span>
  </cs-header-title>

  @if (pageTitleRight && mode !== WorkoutLoadMode.Template) {
    <span header-right class="header-date">{{ pageTitleRight
    }}</span>
  }
  @if (mode === WorkoutLoadMode.Template) {
    <span header-right class="header-date">{{ LL.workouts.detail.plan() }}</span>
  }

  <!-- The rest-timer pill lives in the header, so it hides and shows together with the header
       on a phone instead of staying pinned where the header's bottom edge was. The slot is only
       rendered while the timer shows, so the header does not grow an empty gap otherwise. -->
  @if (restTimerPillShown) {
    <cs-header-content>
      <cs-rest-timer-pill (expand)="onRestTimerExpand()"></cs-rest-timer-pill>
    </cs-header-content>
  }
</cs-header>

<cs-content [loading]="loading || !contentEntered">

  <!-- Exercise list with grouping. #4242: held off the DOM until the page-enter
    slide finishes (contentEntered) so building + rendering the cards doesn't jam
    the animation — the build runs in ionViewDidEnter, after the slide. -->
  @if (contentEntered) {
  <cs-exercise-list [exercises]="exercises" [foldable]="shouldShowExpandArrow()"
    [showMoreButton]="shouldShowMoreOptionsInCollapsed()"
    [showExpandedMoreButton]="true" [hideExpandedFoldIcon]="true" [fillCompletedBadge]="compactFeedback"
    [usesCustomDescription]="usesFinishedHeaderLine"
    (exerciseExpandedMoreClick)="onExerciseExpandedMoreClick($event)"
    (exerciseExpandedChange)="onExerciseListExpandedChange($event)"
    (exerciseHighlightedChange)="onExerciseListHighlightedChange($event)"
    (exerciseMoreOptionsClick)="onExerciseListMoreOptionsClick($event)"
    (exerciseHeaderClick)="onExerciseListHeaderClick($event)">

    <!-- Info cards projected into exercise list -->
    <ng-container header-cards>
      <!-- #4060: explain the calibration "test workout" the first time the user does
        each workout of the split, so the reduced volume doesn't read as a broken
        program -->
      @if (showTestWorkoutExplainer) {
        <cs-info-card type="info">
          {{ LL.workouts.detail.firstWorkoutTestExplainer() }}
          <help-link helpId="@help:workout.RM-strength-test">{{ LL.common.buttons.learnMore() }}</help-link>
        </cs-info-card>
      }

      @if (showWorkoutChangedInfo) {
        <cs-info-card type="info" [dismissible]="true"
          (dismissed)="onWorkoutChangedInfoDismissed()">
          {{ LL.workouts.detail.workoutChangedInfo() }}
        </cs-info-card>
      }

      @if (showViewOnlyInfo && viewOnlyInfoMessage) {
        <cs-info-card type="info"
          [message]="viewOnlyInfoMessage" [dismissible]="true" (dismissed)="onViewOnlyInfoDismissed()">
        </cs-info-card>
      }

      @if (showPendingWorkoutWarning && pendingWorkoutDateLabel) {
        <cs-info-card type="warning" [dismissible]="true"
          (dismissed)="onPendingWorkoutWarningDismissed()">
          {{ LL.workouts.detail.pendingWorkoutWarning() }} <button class="link" (click)="navigateToPendingWorkoutDay()">{{
        pendingWorkoutDateLabel }}</button>. {{ LL.workouts.detail.pendingWorkoutExplanation() }}
      </cs-info-card>
    }
  </ng-container>

  <!-- #4142 review fixes: a finished exercise's line under its name (board F4/F5): the done sets once,
    with no planned-weight brackets, and the same-weight rep chip. The same line folded and opened -->
  <ng-template #descriptionContent let-e>
    @if (getFinishedExerciseSummary(e); as summary) {
      @if (summary.skipped) {
        <span data-e2e-id="e2e-finished-sets">{{ LL.workouts.detail.skipped() }}</span>
      } @else {
        @if (summary.setsText) {
          <span data-e2e-id="e2e-finished-sets">{{ summary.setsText }}</span>
        }
        @if (summary.chip; as chip) {
          <span class="rep-change-chip" data-e2e-id="e2e-finished-chip" [class.up]="chip.direction === RepChangeDirection.Up"
            [class.same]="chip.direction === RepChangeDirection.Same"
            [class.down]="chip.direction === RepChangeDirection.Down">{{ getRepChangeChipText(chip) }}</span>
        }
      }
    }
  </ng-template>

  <!-- Collapsed content template -->
  <ng-template #collapsedContent let-e>
    @if (hasCollapsedContent(e)) {
      <div collapsed class="collapsed-content">
        <!-- drawn only when there are cards, so a folded card leaves no empty gap -->
        @if (getExerciseResolvableStatusLines(e).length > 0) {
          <div class="status-messages">
            @for (line of getExerciseResolvableStatusLines(e); track trackByStatus($index, line)) {
              <cs-info-card type="info" [hideIcon]="true" [dismissible]="false"
                [actionText]="LL.workouts.detail.resolve()" (actionClick)="handlePlateauResolveButton(null, e)"
                >
                {{line.text}}
                @if (line.helpId) {
                  <help-link [helpId]="line.helpId">{{ LL.common.buttons.learnMore() }}</help-link>
                }
              </cs-info-card>
            }
          </div>
        }
      </div>
    }
  </ng-template>

  <!-- Expanded content template -->
  <ng-template #expandedContent let-e>
    <div expanded class="expanded-content">
      <!----- Exercise Button Bar ----->
      <!-- #4142: the exercise menu is in the card header's top right; "Add Note" lives in the bar -->
      <cs-exercise-button-bar (instructionsClick)="handleExerciseTutorialButton(e)"
        (historyClick)="handleExerciseStatsOptionClick(e)"
        [showAddNote]="!hasNotes(e)" (addNoteClick)="openPinnedNotePopover($event, e)">
      </cs-exercise-button-bar>

      <div class="expanded-body">
        <!-- #6428: the client sees once, on the first workout after it, that their coach changed this exercise -->
        @if (getCoachNoteText(e); as coachNote) {
          <cs-info-card type="info" data-e2e-id="e2e-coach-change-note">
            <strong>{{ LL.workouts.detail.coachChanged.title() }}</strong> {{ coachNote }}
          </cs-info-card>
        }
        <!----- Pinned Notes: drawn only when there is a note, so an empty block adds no gap
          ("Add Note" lives in the button bar, #4142) ----->
        @if (hasNotes(e)) {
          <div class="pinned-notes-section">
            <!-- responsive click behavior -->
            <div class="pinned-notes has-notes" [attr.tappable]="hasClientSmallScreen() ? '' : null"
              (click)="hasClientSmallScreen() ? openViewNotesPopover($event, e) : null">
              <cs-icon name="keep"></cs-icon>
              <span class="note-text">
                {{ getNotes(e) }}
                <cs-button class="desktop-only" tertiary icon="edit"
                (click)="openPinnedNotePopover($event, e); $event.stopPropagation()"></cs-button>
              </span>
              <cs-icon class="mobile-only" name="arrow-dropright">
              </cs-icon>
            </div>
          </div>
        }

        <!-- Exercise Sets - Always show all sets -->
        <div class="exercise-sets">
          <!-- Template for collapsed set notes -->
          <!-- #4142 compact feedback: the tinted Rest Timer button. #6575: while a rest counts down it shows
            the time left and a fill for the share still to go, and cannot be tapped. it keeps the normal look
            rather than the disabled one (owner decision, option A of the 2026-10-16 mock-ups) -->
          <ng-template #compactRestTimerButton let-s="set" let-below="below">
            <!-- data-continuous-animation: the fill restarts a transition every second, so the E2E animation
              wait (animation-controller.ts) must not wait for it -->
            <cs-button class="rest-timer-countdown" data-continuous-animation [class.compact-rest-timer]="below" [class.counting]="isRestTimerCounting(s)"
              tinted fit-width icon="time" [endIcon]="isRestTimerCounting(s) ? undefined : 'play'"
              [style.--rest-timer-fill]="getRestTimerFillPercent(s) + '%'"
              data-e2e-id="e2e-start-rest-timer" [label]="getRestTimerButtonText(s)" (click)="onClickStartRestTimer($event, s)">
            </cs-button>
          </ng-template>
          <!-- the status bell beside "Set N" with the compact layout on: a small grey pill with the number of
            hidden messages (board "B"); the folded rows and the open set's header (#6694) all draw it -->
          <ng-template #compactBell let-s="set">
            @if (getTotalStatusMessageCount(s) > 0) {
              <button type="button" class="row-bell" (click)="onClickCompactBell($event, s)">
                <cs-icon name="notifications"></cs-icon>
                @if (getBellBadge(s) >= getBellBadgeMinimum()) {
                  <span class="bell-count" data-e2e="icon-button-badge">{{ getBellBadge(s) }}</span>
                }
              </button>
            }
          </ng-template>
          <ng-template #collapsedSetNotes let-s="set">
            @if (hasSetNotes(s)) {
              <div class="set-notes">
                <cs-icon name="add-notes"></cs-icon>
                <span class="note-text">{{ getSetNotes(s) }}</span>
              </div>
            }
          </ng-template>

          @for (s of e?.sets; track $index; let j = $index) {
            <!-- COMPLETED SET (collapsed) - can reopen -->
            @if (isSetCompleted(s) && j !== e.openSetIndex()) {
              <!-- #4142 review fixes: with the switch on, every done set is one line that unfolds its
                details under it; the row itself does not reopen the set, "Edit Set" in the details does -->
              <div class="completed-set" [attr.data-e2e-id]="'e2e-set-' + (j + 1)"
                [ngClass]="{'incomplete-set': s.logSet.state == LogRecordState.Incomplete, 'has-confirmation': !!getSetProgression(s), 'compact-set': isCompactSetRow(s), 'compact-followup': isCompactFollowupRow(s), 'unfolded': isCompactDoneRow(s) && s.progressUnfolded()}"
                [attr.tappable]="(mode === WorkoutLoadMode.Current && !isCoachManaging && !isCompactDoneRow(s)) ? true : null"
                (click)="isCompactDoneRow(s) ? null : reopenSet(e, j)">
                @if (isCompactDoneRow(s)) {
                  <!-- one line: badge, "Set N" (with the dismissed-message dot), weight × reps, set 1's mark chip, chevron -->
                  <div class="set-header-row compact-set-row" [attr.data-e2e-id]="isCompactSetRow(s) ? 'e2e-compact-set-row' : 'e2e-compact-followup-row'">
                    <button cs-capture-cover data-e2e-id="e2e-set-fold-toggle" [attr.aria-expanded]="s.progressUnfolded()"
                      [attr.aria-label]="getCompactRowAccessibleName(s, j + 1)" (click)="toggleSetProgressFold(s)"></button>
                    <cs-status-icon class="outcome-badge" [tone]="getMarkTone(getSetMark(s))" [textIcon]="true"
                      [size]="StatusIconSize.Small" [icon]="getMarkIcon(getSetMark(s))"
                      [attr.data-mark]="getSetMark(s)" data-e2e-id="e2e-set-mark-badge"></cs-status-icon>
                    <div class="row-values">
                      <span class="set-label">{{ LL.workouts.detail.setOrdinal({number: j + 1}) }}@if (hasDismissedStatusMessage(s)) {<span class="dismissed-dot" data-e2e-id="e2e-dismissed-dot"></span>}</span>
                      <span class="result-today" data-e2e-id="e2e-set-result-today">{{ getCompactSetRow(s) ? formatWeightAndReps(getCompactSetRow(s).done) : getActualSetText(s) }}</span>
                      @if (getMarkChipText(getSetMark(s)); as chipText) {
                        <span class="mark-chip" [attr.data-mark]="getSetMark(s)" data-e2e-id="e2e-set-outcome">{{ chipText }}</span>
                      }
                    </div>
                    <cs-icon fold-chevron [name]="s.progressUnfolded() ? 'arrow-dropup' : 'arrow-dropdown'"></cs-icon>
                  </div>
                  @if (s.progressUnfolded()) {
                    <!-- the details open under the row, with no fill of their own (owner's final pick, board F2) -->
                    <div class="set-detail" data-e2e-id="e2e-set-detail">
                      @if (getSetDetail(s); as detail) {
                        @if (detail.target; as target) {
                          <div class="detail-line">
                            <span class="detail-label">{{ LL.workouts.detail.setProgress.targetLabel() }}</span>
                            <span class="detail-value" data-e2e-id="e2e-detail-target">{{ getTargetRangeText(target) }}</span>
                          </div>
                        }
                        @if (detail.bar; as bar) {
                          <cs-rep-progress-bar class="detail-bar" [min]="0" [max]="bar.scaleMax"
                            [fillFrom]="bar.floor ?? 0" [fillTo]="bar.floor !== undefined ? bar.scaleMax : 0"
                            [markerAt]="bar.lastReps"
                            [markerLabel]="bar.lastReps !== undefined ? LL.workouts.detail.setProgress.lastMarker({ reps: bar.lastReps }) : undefined"
                            [dotAt]="bar.todayReps" [dotTone]="getMarkBarTone(detail.mark)"
                            [showFillLabels]="bar.floor !== undefined" [mergeFillLabels]="true" [sliderLook]="true"
                            [tone]="RepProgressBarTone.Progress">
                          </cs-rep-progress-bar>
                        }
                        @if (detail.lastTime; as lastTime) {
                          <div class="detail-line">
                            <span class="detail-label">{{ LL.workouts.detail.setProgress.lastTimeLabel() }}</span>
                            <span class="detail-value">{{ formatWeightAndReps(lastTime) }}</span>
                          </div>
                        }
                        <!-- the gain sits right under Last time, before Logged at (owner, STOP 1 review) -->
                        @if (detail.overLastTime) {
                          <div class="detail-delta">{{ LL.workouts.detail.setProgress.overLastTimeShort({ count: detail.overLastTime }) }}</div>
                        }
                        @if (detail.loggedAt; as loggedAt) {
                          <div class="detail-line">
                            <span class="detail-label">{{ LL.workouts.detail.setProgress.loggedAtLabel() }}</span>
                            <span class="detail-value">{{ formatLoggedTime(loggedAt) }}</span>
                          </div>
                        }
                        <!-- written like the other lines: the label left, "N+" right (owner, STOP 1 review) -->
                        @if (detail.aimFor) {
                          <div class="detail-line" data-e2e-id="e2e-detail-aim">
                            <span class="detail-label">{{ LL.workouts.detail.setProgress.nextSessionLabel() }}</span>
                            <span class="detail-value">{{ LL.workouts.detail.setProgress.repsOrMore({ reps: detail.aimFor }) }}</span>
                          </div>
                        }
                      } @else {
                        <!-- a later set (or a set 1 that is not a benchmark set): last session's same set -->
                        @if (getLaterSetDetail(s); as later) {
                          @if (later.lastTime; as lastTime) {
                            <div class="detail-line">
                              <span class="detail-label">{{ LL.workouts.detail.setProgress.lastTimeLabel() }}</span>
                              <span class="detail-value">{{ formatWeightAndReps(lastTime) }}</span>
                            </div>
                          }
                          @if (later.overLastTime) {
                            <div class="detail-delta">{{ LL.workouts.detail.setProgress.overLastTimeShort({ count: later.overLastTime }) }}</div>
                          }
                          @if (later.loggedAt; as loggedAt) {
                            <div class="detail-line">
                              <span class="detail-label">{{ LL.workouts.detail.setProgress.loggedAtLabel() }}</span>
                              <span class="detail-value">{{ formatLoggedTime(loggedAt) }}</span>
                            </div>
                          }
                        }
                      }
                      @if (getTotalStatusMessageCount(s) > 0 || canEditSetFromDetail()) {
                        <div class="detail-footer">
                          <div class="title-line">
                            <ng-container *ngTemplateOutlet="compactBell; context: {set: s}"></ng-container>
                          </div>
                          @if (canEditSetFromDetail()) {
                            <cs-button tertiary data-e2e-id="e2e-edit-set" [label]="LL.workouts.detail.setProgress.editSet()"
                              (click)="onClickEditSet($event, e, j)"></cs-button>
                          }
                        </div>
                      }
                    </div>
                  }
                } @else {
                <div class="set-header-row">
                  <span class="set-label">{{ LL.workouts.detail.setOrdinal({number: j + 1}) }}</span>
                  <!-- #4142: a set showing its confirmation carries the check in that badge instead -->
                  @if (s.logSet.state !== LogRecordState.Skipped && !getSetProgression(s)) {
                    <cs-icon name="checkmark-circle" fill></cs-icon>
                  }
                  <cs-note class="set-values">
                    @if (s.logSet.state === LogRecordState.Skipped) {
                      {{ LL.workouts.detail.skipped() }}
                    }
                    @if (s.logSet.state !== LogRecordState.Skipped && !hasSetOverride(s)) {
                      {{ getActualSetText(s) }}
                    }
                    @if (s.logSet.state !== LogRecordState.Skipped && hasSetOverride(s)) {
                      <span class="planned">{{ getPlannedSetText(s) }}</span>
                      {{ getActualSetText(s) }}
                    }
                  </cs-note>
                  @if (getTotalStatusMessageCount(s) > 0) {
                    <cs-icon-button icon="notifications" small
                      [attr.link-color]="isBellLinkColored(s) ? '' : null"
                      [badge]="getBellBadge(s)" [badgeMinimum]="getBellBadgeMinimum()"
                      (click)="onNotificationBellClick(s)">
                    </cs-icon-button>
                  }
                </div>
                <!-- #4142: the benchmark set's confirmation, derived from its own stored data -->
                @if (getSetProgression(s); as progression) {
                  <div class="set-confirmation" [class.progress]="progression.headline !== SetProgressionHeadline.Logged">
                    <div class="confirmation-badge">
                      <cs-icon name="checkmark"></cs-icon>
                    </div>
                    <div class="confirmation-headline">{{ getProgressionHeadlineText(progression) }}</div>
                    <p class="confirmation-body">
                      <span class="confirmation-line">{{ getRepsAtWeightText(progression.done) }}@if (progression.overLastTime) { &mdash; <span class="over-last-time">{{ LL.workouts.detail.setProgress.overLastTime({ count: progression.overLastTime }) }}</span>}.@if (progression.lastTimeReps !== undefined) { {{ LL.workouts.detail.setProgress.lastTimeYouDid({ reps: progression.lastTimeReps }) }}}</span>
                      @if (progression.aimFor) {
                        <span class="confirmation-line">{{ LL.workouts.detail.setProgress.nextSessionAimFor({ reps: progression.aimFor }) }}</span>
                      }
                    </p>
                    @if (progression.bars; as bars) {
                      <div class="confirmation-bars">
                        <div class="bar-row">
                          <span class="bar-label">{{ LL.workouts.detail.setProgress.lastTimeLabel() }}</span>
                          <cs-rep-progress-bar [min]="0" [max]="bars.scaleMax" [fillFrom]="0" [fillTo]="bars.lastReps"></cs-rep-progress-bar>
                          <span class="bar-value">{{ bars.lastReps }}</span>
                        </div>
                        <div class="bar-row today">
                          <span class="bar-label">{{ LL.workouts.detail.setProgress.todayLabel() }}</span>
                          <cs-rep-progress-bar [min]="0" [max]="bars.scaleMax" [fillFrom]="0" [fillTo]="bars.todayReps"
                            [tone]="bars.tone"></cs-rep-progress-bar>
                          <span class="bar-value" [class.progress]="bars.tone === RepProgressBarTone.Progress">{{ bars.todayReps }}</span>
                        </div>
                      </div>
                    }
                    <!-- #6014: (re)start the rest timer from full; only on the latest set of today's workout -->
                    @if (isRestTimerStartOffered(s)) {
                      <cs-button tertiary fit-width data-e2e-id="e2e-start-rest-timer"
                        [label]="getStartRestTimerText(s)" (click)="onClickStartRestTimer($event, s)"></cs-button>
                    }
                  </div>
                }
                }
                <!-- PostSetStatus alert boxes for collapsed completed set -->
                @if (getSetPostStatusTextLines(s).length > 0) {
                  <div class="status-messages">
                    @for (line of getSetPostStatusTextLines(s); track trackByStatus($index, line)) {
                      <cs-info-card type="info" [hideIcon]="true" [dismissible]="!isResolvableStatus(line.status)"
                        (dismissed)="dismissStatus(line.statusObject)"
                        [actionText]="isResolvableStatus(line.status) ? LL.workouts.detail.resolve() : null"
                        (actionClick)="handlePlateauResolveButton(null, s.uiExercise)"
                        >
                        {{line.text}}
                        @if (line.helpId) {
                          <help-link [helpId]="line.helpId">{{ LL.common.buttons.learnMore() }}</help-link>
                        }
                      </cs-info-card>
                    }
                  </div>
                }
                <!-- Set Notes for collapsed set -->
                <ng-container *ngTemplateOutlet="collapsedSetNotes; context: {set: s}"></ng-container>
              </div>
              <!-- #4142 compact feedback: while set 1 is folded, the Rest Timer button sits directly below it;
                #6575: so does it below a later set's row, while that set is the latest logged one -->
              @if (isCompactRestTimerBelowRow(s)) {
                <ng-container *ngTemplateOutlet="compactRestTimerButton; context: {set: s, below: true}"></ng-container>
              }
            }
            <!-- FUTURE SET (not ready - collapsed, disabled) -->
            @if (isSetFuture(e, j) && j !== e.openSetIndex()) {
              <div class="future-set" [class.compact-upcoming]="compactFeedback" [attr.data-e2e-id]="'e2e-set-' + (j + 1)"
                >
                <!-- #4142 review fixes: an upcoming set is a neutral row with its number in the badge -->
                @if (compactFeedback) {
                  <span class="set-number-circle">{{ j + 1 }}</span>
                }
                <span class="set-label">{{ LL.workouts.detail.setOrdinal({number: j + 1}) }}</span>
              </div>
            }
            <!-- READY SET (not yet started - can be clicked to open) -->
            @if (isSetReady(e, j) && !isSetCompleted(s) && j !== e.openSetIndex()) {
              <div class="ready-set" [class.compact-upcoming]="compactFeedback" [attr.data-e2e-id]="'e2e-set-' + (j + 1)"
                [attr.tappable]="(mode === WorkoutLoadMode.Current && !isCoachManaging) ? true : null" (click)="openSet(e, j, 'focus')">
                <div class="set-header-row">
                  @if (compactFeedback) {
                    <span class="set-number-circle">{{ j + 1 }}</span>
                  }
                  <span class="set-label">{{ LL.workouts.detail.setOrdinal({number: j + 1}) }}</span>
                  @if (getTotalStatusMessageCount(s) > 0) {
                    <cs-icon-button icon="notifications" small
                      [attr.link-color]="isBellLinkColored(s) ? '' : null"
                      [badge]="getBellBadge(s)" [badgeMinimum]="getBellBadgeMinimum()"
                      (click)="onNotificationBellClick(s)">
                    </cs-icon-button>
                  }
                </div>
                <!-- Set Notes for collapsed set -->
                <ng-container *ngTemplateOutlet="collapsedSetNotes; context: {set: s}"></ng-container>
              </div>
            }
            <!-- CURRENT SET (expanded) - active set being edited -->
            @if (j == e.openSetIndex()) {
              <div class="current-set" [attr.data-e2e-id]="'e2e-set-' + (j + 1)">
                <!-- Set Header -->
                <div class="set-header">
                  @if (compactFeedback) {
                    <!-- #6694: the same bell pill right after "Set N"; a reopened done set also shows what its
                      folded row shows: the mark badge and, on set 1, the mark chip (#4142 review fixes) -->
                    <div class="set-title-group">
                      @if (getOpenSetOutcome(s); as mark) {
                        <cs-status-icon class="outcome-badge" [tone]="getMarkTone(mark)" [textIcon]="true"
                          [size]="StatusIconSize.Small" [icon]="getMarkIcon(mark)" [attr.data-mark]="mark"></cs-status-icon>
                      }
                      <div class="title-line">
                        <span class="set-label">{{ LL.workouts.detail.setOrdinal({number: j + 1}) }}</span>
                        <ng-container *ngTemplateOutlet="compactBell; context: {set: s}"></ng-container>
                        @if (getOpenSetOutcome(s); as mark) {
                          @if (getMarkChipText(mark); as chipText) {
                            <span class="mark-chip" [attr.data-mark]="mark" data-e2e-id="e2e-open-set-outcome">{{ chipText }}</span>
                          }
                        }
                      </div>
                    </div>
                  } @else {
                    <span class="set-label">{{ LL.workouts.detail.setOrdinal({number: j + 1}) }}</span>
                  }
                  <div class="set-header-icons">
                    @if (!compactFeedback && getTotalStatusMessageCount(s) > 0) {
                      <cs-icon-button icon="notifications" small
                        [attr.link-color]="isBellLinkColored(s) ? '' : null"
                      [badge]="getBellBadge(s)" [badgeMinimum]="getBellBadgeMinimum()"
                        (click)="onNotificationBellClick(s)">
                      </cs-icon-button>
                    }
                    @if (!s.inEditMode() && (s.isBasicInstruction() || ((s.requireStrengthInput() || s.requireRepsInput()) && e.equipment) || isSetBW(s))) {
                      @if (compactFeedback) {
                        <!-- #4142 compact feedback: an info toggle in the bell's colour, filled while the lines show -->
                        <!-- x-small (18px): the owner asked for a smaller info icon at the STOP 1 review -->
                        <cs-icon-button icon="information-circle" x-small data-e2e-id="e2e-set-info-toggle"
                          [fill]="showSetInstructions || showBwHelpText" [pressed]="showSetInstructions || showBwHelpText"
                          [ariaLabel]="LL.workouts.detail.setInstructionsToggle()"
                          (click)="toggleSetInstructions()">
                        </cs-icon-button>
                      } @else {
                        <cs-icon-button icon="help-circle" small
                          [attr.link-color]="showSetInstructions || showBwHelpText ? null : ''"
                          (click)="toggleSetInstructions()">
                        </cs-icon-button>
                      }
                    }
                    <!-- #6694: a reopened done set gets an arrow that folds it back without saving -->
                    @if (canCollapseOpenSet(s)) {
                      <cs-icon-button class="collapse-set" icon="arrow-dropup" small data-e2e-id="e2e-collapse-set"
                        [ariaLabel]="LL.workouts.detail.collapseSet()"
                        (click)="onCollapseSetButtonClicked($event, s)">
                      </cs-icon-button>
                    }
                  </div>
                </div>
                <div class="set-body">
                  <!-- PreSetStatus Alert Boxes -->
                  @if (getSetPreStatusTextLines(s).length > 0) {
                    <div class="status-messages">
                      @for (line of getSetPreStatusTextLines(s); track trackByStatus($index, line)) {
                        <cs-info-card type="info" [hideIcon]="true" (dismissed)="dismissStatus(line.statusObject)"
                          >
                          {{line.text}}
                          @if (line.helpId) {
                            <help-link [helpId]="line.helpId">{{ LL.common.buttons.learnMore() }}</help-link>
                          }
                        </cs-info-card>
                      }
                    </div>
                  }
                  <!----- Set Notes (like pinned notes but without add button) ----->
                  @if (hasSetNotes(s)) {
                    <div class="set-notes-section">
                      <div class="set-notes has-notes" [attr.tappable]="hasClientSmallScreen() ? '' : null"
                        (click)="hasClientSmallScreen() ? openViewSetNotePopover($event, s) : null">
                        <cs-icon name="add-notes"></cs-icon>
                        <span class="note-text">
                          {{ getSetNotes(s) }}
                          <cs-button class="desktop-only" tertiary icon="edit"
                          (click)="openSetNotePopover($event, s); $event.stopPropagation()"></cs-button>
                        </span>
                        <cs-icon class="mobile-only" name="arrow-dropright"></cs-icon>
                      </div>
                    </div>
                  }
                  @if ((s.instructionsText() && (!s.isBasicInstruction() || showSetInstructions)) || (showSetInstructions && (s.requireStrengthInput() || s.requireRepsInput()) && e.equipment)) {
                    <div class="instructions-notes"
                      >
                      <!-- Instructions with Learn more link embedded (basic ones like "try to achieve target reps" are toggleable) -->
                      @if (s.instructionsText()) {
                        <cs-note>
                          <cs-markdown slim [textMarkdown]="s.instructionsText()"></cs-markdown>
                        </cs-note>
                      }
                      <!-- Input requirements note - toggleable -->
                      @if (showSetInstructions && (s.requireStrengthInput() || s.requireRepsInput()) && e.equipment) {
                        <cs-note>
                          {{ s.requireStrengthInput() && s.requireRepsInput() ? setInstructionsTextInputRepsAndStrength :
                          (s.requireRepsInput() ? setInstructionsTextInputReps : setInstructionsTextInputStrength) }}
                        </cs-note>
                      }
                    </div>
                  }
                  <!-- Last Attempt -->
                  @if (s.lastAttemptValue()) {
                    <div class="last-attempt">
                      <div class="last-attempt-days">{{s.lastAttemptDays()}}</div>
                      <div class="last-attempt-value">{{s.lastAttemptValue()}}</div>
                    </div>
                  }
                  <!-- #4142: the rep range, and what last session did -->
                  @if (getRepRangeDisplay(s).range; as range) {
                    <!-- #4142 review fixes: with the switch on, a range after a weight increase has no tick, and
                      labels that would touch merge into one -->
                    <cs-rep-progress-bar class="set-rep-range" [min]="0" [max]="range.target"
                      [fillFrom]="range.floor" [fillTo]="range.target" [markerAt]="range.lastReps"
                      [markerLabel]="range.lastReps !== undefined ? LL.workouts.detail.setProgress.lastMarker({ reps: range.lastReps }) : undefined"
                      [showFillLabels]="true" [mergeFillLabels]="compactFeedback" [sliderLook]="compactFeedback" [tone]="RepProgressBarTone.Progress">
                    </cs-rep-progress-bar>
                  }
                  @if (getOpenSetLastSession(s); as lastSession) {
                    <div class="set-reference-line set-last-session">
                      <span class="reference-label">{{ LL.workouts.detail.setProgress.lastSession() }}</span>
                      <span class="reference-value">{{ formatWeightAndReps(lastSession) }}</span>
                    </div>
                  }
                  <!-- Set Inputs Area: Inputs + Button -->
                  <div class="set-inputs-area">
                    <div class="set-inputs-wrapper">
                      <!-- Main Inputs Row: Weight + Reps -->
                      <div class="set-inputs-row">
                        <div class="inputs-container">
                          @if (!e.equipment) {
                            <div class="disabling-cover"></div>
                          }
                          <!-- Weight Input -->
                          <cs-form-field class="weight-field">
                            <cs-label>{{ s.logSet.stats.weight_unit === 'lb' ? LL.workouts.detail.units.lb()
                            : LL.workouts.detail.units.kg() }}</cs-label>
                            <cs-input-measure #weightInput x-small panel-field type="addedweight_kg" input-sequence
                              [disabled]="!isSetEditable(s)" [(userUnit)]="s.logSet.stats.weight_unit"
                              [(canonValue)]="s.logSet.stats.weight_kg" (editDone)="weightChanged(s)"
                              (userUnitChange)="onWeightUnitChanged(e, s)" [preselectValues]="e.possibleWeightValues_kg"
                              id="weight-input-{{exercises.indexOf(e)}}-{{j}}" [pairedWith]="repsInput"
                              pairRole="primary">
                            </cs-input-measure>
                          </cs-form-field>
                          <!-- Reps Input -->
                          <cs-form-field class="reps-field">
                            <cs-label>{{ LL.workouts.detail.repsLabel() }}</cs-label>
                            <cs-input-measure #repsInput x-small panel-field input-sequence type="reps" [showUnit]="false"
                              [disabled]="!isSetEditable(s)" [(canonValue)]="s.logSet.stats.reps" (editDone)="repsChanged(s)"
                              [preselectValues]="possibleRepValues"
                              [placeholder]="getRepsPlaceholder(s)"
                              [suggestedPreselectValue]="getRepsLandingValue(s)"
                              id="reps-input-{{exercises.indexOf(e)}}-{{j}}" [pairedWith]="weightInput"
                              pairRole="secondary">
                            </cs-input-measure>
                          </cs-form-field>
                        </div>
                      </div>
                      <!-- Myo-Reps Section -->
                      @if (s.logSet.miniSets && s.logSet.miniSets.length > 0) {
                        <div class="myo-reps-section">
                          @if (e.equipment===undefined) {
                            <div class="disabling-cover"></div>
                          }
                          <div class="myo-reps-row">
                            <cs-form-field class="myo-field">
                              <cs-label>{{ LL.workouts.detail.myoReps() }}</cs-label>
                              <div class="myo-inputs">
                                @for (m of s.logSet.miniSets; track $index; let iM = $index) {
                                  @if (iM > 0) {
                                    <span class="myo-plus">+</span>
                                  }
                                  <cs-input-measure class="myo-input" x-small panel-field type="myo-reps"
                                    [disabled]="!isSetEditable(s)" [(canonValue)]="m.reps"
                                    [preselectValues]="possibleMyoRepsValues">
                                  </cs-input-measure>
                                }
                              </div>
                            </cs-form-field>
                          </div>
                        </div>
                      }
                    </div>
                    <!-- Single action button outside inputs wrapper -->
                    <div class="set-action-wrapper">
                      <!-- Checkmark icon for completed set (no changes yet) -->
                      @if (isSetCompleted(s) && !hasSetValuesChanged(s)) {
                        <cs-icon class="completed-checkmark" name="checkmark"
                        ></cs-icon>
                      }
                      <!-- Log Button - show when fresh set OR completed set with modifications -->
                      @if (!isCoachManaging && (!isSetCompleted(s) || hasSetValuesChanged(s))) {
                        <!-- #4142: a worded button only as wide as its label; the Reps field takes the rest of the row (mock boards A/H/J).
                          phones get a one-word label: the full ones do not fit beside the inputs in every language.
                          keep-space-when-borrowed: while the virtual keyboard shows its own Log button, this one is
                          hidden but keeps its space, so the Reps field does not change width -->
                        <cs-button class="log-set-button" small field primary input-sequence keep-space-when-borrowed
                          id="e2e-set-{{j+1}}-next"
                          [disabled]="!isSetComplete(s)" (click)="onClickSetDone($event, s)">
                          @if (hasClientSmallScreen()) {
                            {{ isSetCompleted(s) ? LL.workouts.buttons.saveShort() : LL.workouts.buttons.logShort() }}
                          } @else {
                            {{ isSetCompleted(s) ? LL.workouts.buttons.saveSet() : (isLastSet(e, j) ? LL.workouts.buttons.completeSet() :
                            LL.workouts.buttons.logSet()) }}
                          }
                        </cs-button>
                      }
                    </div>
                  </div>
                  <!-- #6151: the field shows the equipment's lightest weight, which is above the plan's target -->
                  @if (s.equipmentFloorHint; as floorHint) {
                    <div class="equipment-floor-hint">
                      <span class="equipment-floor-hint-text">{{ floorHint.text }}</span>
                      <cs-tooltip iconType="info"
                        [title]="floorHint.title"
                        [mobileText]="floorHint.explanation"
                        [buttonText]="LL.workouts.detail.equipmentFloor.button()"
                        [buttonCallback]="getConfigurePlatesCallback(e, s)" (click)="$event.stopPropagation()">
                        {{ floorHint.explanation }}
                      </cs-tooltip>
                    </div>
                  }
                  <!-- Bodyweight Help Text (shown after inputs for BW exercises) -->
                  @if (isSetBW(s) && (showSetInstructions || showBwHelpText)) {
                    <cs-note class="bw-help-text">
                      @if (s.showBwHint()) {
                        <hint-bubble [dismiss]="true" target=".bw-help-text" once="added-weight-bw">
                          <p [innerHTML]="LL.workouts.detail.weightExplanations.addedWeightExplanation()"></p>
                        </hint-bubble>
                      }
                      {{ LL.workouts.detail.bwHelpText() }}
                      <help-link helpId="@help:workout.your-bw-plus">{{ LL.common.buttons.learnMore() }}</help-link>
                    </cs-note>
                  }
                  <!-- PostSetStatus Alert Boxes -->
                  @if (getSetPostStatusTextLines(s).length > 0) {
                    <div class="status-messages">
                      @for (line of getSetPostStatusTextLines(s); track trackByStatus($index, line)) {
                        <cs-info-card type="info" [hideIcon]="true" [dismissible]="!isResolvableStatus(line.status)"
                          (dismissed)="dismissStatus(line.statusObject)"
                          [actionText]="isResolvableStatus(line.status) ? LL.workouts.detail.resolve() : null"
                          (actionClick)="handlePlateauResolveButton(null, s.uiExercise)"
                          >
                          {{line.text}}
                          @if (line.helpId) {
                            <help-link [helpId]="line.helpId">{{ LL.common.buttons.learnMore() }}</help-link>
                          }
                        </cs-info-card>
                      }
                    </div>
                  }
                  <!-- Equipment Setup -->
                  <div class="equipment-setup">
                    @if (s.showBarbellHint()) {
                      <hint-bubble [dismiss]="true" target=".equipment-row"
                        once="barbell-included">
                        <p [innerHTML]="LL.workouts.detail.weightExplanations.totalWeightExplanation()"></p>
                      </hint-bubble>
                    }
                    <!-- Row 1: Icon + Name + Arrow (clickable row) -->
                    <!-- #4142 compact feedback: with the switch on, the row is one outlined box with the setup parts on one wrapping line under the name -->
                    <div class="equipment-row" [class.equipment-box]="compactFeedback"
                      [class.has-error]="compactFeedback && !!s.setupErrorWeight"
                      [class.not-chosen]="compactFeedback && !hasChosenEquipment(e)"
                      [class.has-setup-line]="compactFeedback && !!getEquipmentSetupLine(e, s)">
                      <button cs-capture-cover (click)="configurePlates(e, s, $event)"></button>
                      <div class="configure-plates-holder">
                        <!-- Normal state: plain exercise icon -->
                        @if (!s.setupErrorWeight) {
                          <cs-icon name="exercise" [attr.flashing]="!e.equipment ? '' : null">
                            @if (!e.equipment) {
                              <hint-bubble [dismiss]="true" target=".configure-plates-holder">
                                <p>{{ LL.workouts.detail.selectEquipmentHere() }}</p>
                              </hint-bubble>
                            }
                          </cs-icon>
                        }
                        <!-- Warning state: warning icon with tooltip -->
                        @if (s.setupErrorWeight) {
                          <cs-tooltip iconType="warning"
                            [title]="LL.workouts.detail.equipmentWarning.title()"
                            [mobileText]="LL.workouts.detail.equipmentWarning.mobileMessage({ error: s.setupErrorWeight })"
                            [buttonText]="LL.workouts.detail.equipmentWarning.button()"
                            [buttonCallback]="getConfigurePlatesCallback(e, s)" (click)="$event.stopPropagation()">
                            {{ LL.workouts.detail.equipmentWarning.messagePart1({ error: s.setupErrorWeight }) }}<a class="tooltip-link" (click)="configurePlates(e, s); $event.stopPropagation()">{{ LL.workouts.detail.equipmentWarning.messageLink() }}</a>{{ LL.workouts.detail.equipmentWarning.messagePart2() }}
                          </cs-tooltip>
                        }
                      </div>
                      @if (compactFeedback) {
                        <div class="equipment-box-text">
                          <span class="equipment-name">{{ hasChosenEquipment(e) ? e.equipment.name : LL.workouts.detail.equipment.chooseEquipmentLabel() }}</span>
                          @if (getEquipmentSetupLine(e, s); as setupLine) {
                            <span class="equipment-setup-line">{{ setupLine }}</span>
                          }
                        </div>
                      } @else {
                        <span class="equipment-name">{{ e.equipment?.name }}</span>
                      }
                      <cs-icon name="arrow-dropright"></cs-icon>
                    </div>
                    <!-- Row 2: Setup text -->
                    @if (!compactFeedback && s.setupTextParts?.length) {
                      <div class="setup-text-parts">
                        @for (part of s.setupTextParts; track part.label) {
                          <cs-note>
                            <span class="setup-label">{{ part.label }}</span><span class="setup-value">{{ part.value
                          }}</span>
                        </cs-note>
                      }
                    </div>
                  }
                </div>
              </div>
            </div>
          }
        }
      </div>

      <!-- #4142 compact feedback: a finished exercise's last workout and next target (board "Finished exercise — unfolded") -->
      @if (isCompactFinishedExercise(e) && getFinishedExerciseSummary(e); as summary) {
        @if (summary.lastWorkoutSetsText || summary.nextReps) {
          <div class="finished-lines" data-e2e-id="e2e-finished-lines">
            @if (summary.lastWorkoutSetsText) {
              <div class="finished-line">
                <cs-icon name="time"></cs-icon>
                <span>{{ LL.workouts.detail.finishedExercise.lastWorkoutLine({ sets: summary.lastWorkoutSetsText }) }}</span>
              </div>
            }
            @if (summary.nextReps) {
              <div class="finished-line">
                <cs-icon name="trending-up"></cs-icon>
                <span>{{ LL.workouts.detail.finishedExercise.nextTargetLine({ reps: summary.nextReps }) }}</span>
              </div>
            }
          </div>
        }
      }

      <!-- Last Workout (exercise level) -->
      @if (isEditableMode()) {
        @if (e.lastWorkoutInfo()) {
          <!-- #4142 compact feedback: with the switch on, the row is the same outlined box as the equipment row -->
          <div class="last-workout" [class.last-workout-box]="compactFeedback"
            [class.expanded]="e.lastWorkoutExpanded()">
            <div class="last-workout-row">
              <button cs-capture-cover (click)="handleLastWorkoutClick(e, $event)"></button>
              @if (hasPeriodization(e) && !hasClientSmallScreen()) {
                <cs-tooltip [title]="LL.workouts.detail.lastWorkout()"
                  iconType="time">
                  {{ LL.workouts.detail.lastWorkoutSameIntensity() }}
                </cs-tooltip>
              }
              @if (!hasPeriodization(e) || hasClientSmallScreen()) {
                <cs-icon name="time"></cs-icon>
              }
              <span class="last-workout-label">{{ LL.workouts.detail.lastWorkout() }} - {{ e.lastWorkoutDate()
              }}</span>
              @if (e.lastWorkoutHasNotes()) {
                <cs-icon name="article"></cs-icon>
              }
              <cs-icon class="arrow-mobile" name="arrow-dropright"></cs-icon>
              <cs-icon class="arrow-desktop"
              [name]="e.lastWorkoutExpanded() ? 'arrow-dropdown' : 'arrow-dropright'"></cs-icon>
            </div>
            <cs-log-detail-content class="last-workout-content" [sets]="e.lastWorkoutSets()"></cs-log-detail-content>
          </div>
        } @else if (e.lastWorkoutLoading()) {
          <!-- last-workout history loads off the critical path; show a spinner until ready -->
          <div class="last-workout loading" [class.last-workout-box]="compactFeedback">
            <div class="last-workout-row">
              <cs-icon name="time"></cs-icon>
              <span class="last-workout-label">{{ LL.workouts.detail.lastWorkout() }}</span>
              <cs-spinner small></cs-spinner>
            </div>
          </div>
        }
      }

    </div>
  </div>
</ng-template>

<!-- Workout Complete Card. #4142 compact feedback: a summary of the workout instead (board "Summary B") -->
<!-- two sibling blocks, each with one root element: content projection into the list's footer slot
  only works for a block whose root is a single element, and that root is a cs-card because
  cs-card-list lays out only card components. The summary also shows on a finished workout opened
  again (#6536); the switch-off card and the Close button only in the visit that finished it -->
@if (isWorkoutFinished() && !isCoachManaging && compactFeedback && workoutSummary; as summary) {
  <cs-card footer-cards class="workout-summary" id="workout-complete" data-e2e-id="e2e-workout-summary">
    <div class="summary-heading">
      <cs-status-icon [tone]="StatusIconTone.Success"></cs-status-icon>
      <div class="summary-heading-text">
        <span class="summary-title">{{ LL.workouts.detail.summary.title() }}</span>
        <span class="summary-totals" data-e2e-id="e2e-workout-summary-totals">{{ getWorkoutSummaryTotalsText(summary) }}</span>
        @if (getWorkoutSummaryTestMessage(); as testMessage) {
          <span class="summary-test-message">{{ testMessage }}</span>
        }
      </div>
    </div>
    @if (getWorkoutSummaryHighlight(summary); as highlight) {
      <div class="summary-highlight">
        <cs-icon name="trophy"></cs-icon>
        <span>
          @if (highlight.records) {
            <span class="highlight-records">{{ highlight.records }}</span>
          }
          @if (highlight.records && highlight.progressed) {
            {{ LL.workouts.detail.summary.and() }}
          }
          @if (highlight.progressed) {
            {{ highlight.progressed }}
          }
        </span>
      </div>
    }
    @if (summary.rows.length > 0) {
      <div class="summary-rows">
        @for (row of summary.rows; track $index) {
          <div class="summary-row" data-e2e-id="e2e-workout-summary-row">
            <!-- #4142 review fixes: the badge carries set 1's mark, as on its own row -->
            <cs-status-icon [tone]="getMarkTone(row.mark)" [textIcon]="true" [attr.data-mark]="row.mark"
              [size]="StatusIconSize.Small" [icon]="getMarkIcon(row.mark)"></cs-status-icon>
            <div class="summary-row-name">
              <span class="exercise-name">{{ row.name }}</span>
              @if (row.personalRecord) {
                <span class="personal-record"><cs-icon name="trophy"></cs-icon>{{ LL.workouts.detail.summary.personalRecord() }}</span>
              }
            </div>
            <div class="summary-row-result">
              <span class="result-today">{{ formatWeightAndReps(row.today) }}</span>
              @if (row.lastTime) {
                <span class="result-last">{{ LL.workouts.detail.setProgress.lastShort({ value: formatWeightAndReps(row.lastTime) }) }}</span>
              }
            </div>
          </div>
        }
      </div>
    }
  </cs-card>
}
@if (complete && !isCoachManaging && !(compactFeedback && workoutSummary)) {
  <cs-info-card footer-cards type="success" [message]="completionMessage"
    [dismissible]="false">
  </cs-info-card>
}
</cs-exercise-list>
  }
</cs-content>

@if (complete && !isCoachManaging) {
  <cs-page-action-buttons>
    <cs-button medium [label]="LL.workouts.buttons.close()" (click)="handleClose($event)"></cs-button>
    @if (settings.testMode) {
      <cs-button medium [label]="LL.workouts.buttons.nextDay()"
      (click)="handleFakeNextDay()"></cs-button>
    }
  </cs-page-action-buttons>
}

@if (restTimerExpanded) {
  <cs-rest-timer-expanded (collapse)="onRestTimerCollapse()"></cs-rest-timer-expanded>
}